import React, { FC, useMemo } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { Slider, Button, Popover } from 'antd';
import moment from 'moment';

type TPoint = {
  time?: string;
  lat: number;
  lon: number;
  alt?: number;
  mapId?: string | number;
};

interface IProps {
  points: TPoint[];
  value: [number, number] | null;
  onChange: (range: [number, number]) => void;
  onReset?: () => void;
  employeeName?: string;
}

const useStyles = makeStyles(() => ({
  container: {
    position: 'fixed' as const,
    left: 380,
    right: 0,
    bottom: 72,
    zIndex: 1000,
    background: 'rgba(255,255,255,0.95)',
    borderRadius: 0,
    padding: '10px 16px',
    boxShadow: '0 -2px 8px rgba(0,0,0,0.08)',
    backdropFilter: 'saturate(180%) blur(6px)',
    borderTop: '1px solid rgba(0,0,0,0.06)'
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 6,
    gap: 8
  },
  label: {
    fontSize: 12,
    color: '#626C77'
  },
  sliderWrap: {
    padding: '0 20px'
  },
  histWrap: {
    height: 28,
    marginTop: 6,
    padding: '0 20px'
  },
  hist: {
    display: 'flex',
    alignItems: 'flex-end',
    height: '100%',
    gap: 1
  },
  bar: {
    background: '#ccd3da',
    flex: 1,
    borderRadius: 1,
    pointerEvents: 'auto',
    cursor: 'pointer',
    transition: 'background-color .15s ease',
    '&:hover': {
      background: '#b9c2cc'
    }
  }
}));

function formatTs(ts: number) {
  return moment(ts).format('L HH:mm:ss');
}

const TimelineBrush: FC<IProps> = ({ points, value, onChange, onReset, employeeName }) => {
  const classes = useStyles();

  const [minTime, maxTime] = useMemo<[number, number] | [null, null]>(() => {
    const times = points
      .map(p => (p.time ? moment(p.time).valueOf() : NaN))
      .filter(v => Number.isFinite(v)) as number[];
    if (!times.length) return [null, null];
    return [Math.min(...times), Math.max(...times)];
  }, [points]);

  const marks = useMemo(() => {
    if (minTime == null || maxTime == null) return {};
    const span = maxTime - minTime;
    const res: Record<number, any> = {};
    // Place a few evenly spaced marks (5 ticks)
    const ticks = 4;
    res[minTime] = { style: { color: '#64727c' }, label: 'Старт' };
    for (let i = 1; i <= ticks; i++) {
      const t = Math.round(minTime + (span * i) / (ticks + 1));
      res[t] = { style: { color: '#9aa3ab' }, label: '' };
    }
    res[maxTime] = { style: { color: '#64727c', marginRight: '10px' }, label: 'Финиш' };
    return res;
  }, [minTime, maxTime]);

  const { density, binsMeta } = useMemo(() => {
    if (minTime == null || maxTime == null)
      return {
        density: [] as number[],
        binsMeta: [] as { start: number; end: number; count: number; sample: number[] }[]
      };

    const times = points
      .map(p => (p.time ? moment(p.time).valueOf() : NaN))
      .filter(v => Number.isFinite(v)) as number[];
    if (!times.length) return { density: [], binsMeta: [] };

    const span = maxTime - minTime || 1;
    const approxMinutes = Math.max(1, Math.floor(span / (60 * 1000)));
    const binCount = Math.min(80, Math.max(20, approxMinutes));

    const timesByBin: number[][] = Array.from({ length: binCount }, () => []);
    for (const t of times) {
      const idx = Math.min(
        binCount - 1,
        Math.max(0, Math.floor(((t - minTime) / span) * binCount))
      );
      timesByBin[idx].push(t);
    }
    const counts = timesByBin.map(arr => arr.length);
    const max = Math.max(1, ...counts);
    const density = counts.map(c => c / max);
    const binsMeta = timesByBin.map((arr, i) => ({
      start: Math.round(minTime + (span * i) / binCount),
      end: Math.round(minTime + (span * (i + 1)) / binCount),
      count: arr.length,
      sample: arr.length ? [...arr].sort((a, b) => a - b) : []
    }));
    return { density, binsMeta };
  }, [points, minTime, maxTime]);

  if (minTime == null || maxTime == null) return null;

  let current: [number, number];
  if (value) {
    const a = Math.min(Math.max(value[0], minTime), maxTime);
    const b = Math.min(Math.max(value[1], minTime), maxTime);
    current = a <= b ? [a, b] : [b, a];
  } else {
    current = [minTime, maxTime];
  }

  return (
    <div className={classes.container}>
      <div className={classes.header}>
        <div className={classes.label}>
          Выделение диапазона трека{employeeName ? `: ${employeeName}` : ''}
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <div className={classes.label}>
            {formatTs(current[0])} - {formatTs(current[1])}
          </div>
          {onReset && (
            <Button size="small" onClick={onReset}>
              Сбросить
            </Button>
          )}
        </div>
      </div>
      <div className={classes.sliderWrap}>
        <Slider
          range={{ draggableTrack: true }}
          min={minTime}
          max={maxTime}
          tooltip={{ formatter: (v?: number) => (typeof v === 'number' ? formatTs(v) : '') }}
          value={current}
          step={Math.max(1, Math.floor((maxTime - minTime) / 100))} // adaptive step
          marks={marks}
          onChange={(next: any) => {
            if (Array.isArray(next) && next.length === 2) {
              const [a, b] = next as [number, number];
              onChange([Math.min(a, b), Math.max(a, b)]);
            }
          }}
        />
      </div>
      <div className={classes.histWrap}>
        <div className={classes.hist}>
          {density.map((v, i) => {
            const meta = binsMeta[i];
            const title = meta?.count ? `Точек: ${meta.count}` : 'Нет точек';
            const content = (
              <div>
                <div>
                  Интервал: {formatTs(meta?.start ?? minTime)} — {formatTs(meta?.end ?? maxTime)}
                </div>
                {meta?.count > 0 && (
                  <div>
                    <ul style={{ margin: 0, paddingLeft: 16 }}>
                      {meta.sample.map((t, j) => (
                        <li key={j}>{formatTs(t)}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            );
            return (
              <Popover
                key={i}
                placement="top"
                content={content}
                title={title}
                overlayInnerStyle={{ maxHeight: 300, overflowY: 'auto' }}
              >
                <div
                  className={classes.bar}
                  style={{ height: `${Math.round(v * 100)}%` }}
                  onClick={() => onChange([meta?.start ?? minTime, meta?.end ?? maxTime])}
                />
              </Popover>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default TimelineBrush;
